import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus, Search, ShieldAlert, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ArrowUpDown, Filter, RefreshCw, Box, X, Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme } from '../lib/swalTheme';
import { usePermissions } from './usePermissions';
import { useFavoriteFilters } from './useFavoriteFilters';
import { useFontSize } from './FontSizeContext';

const HazardList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const navigate = useNavigate();
  const location = useLocation();
  const { canCreate } = usePermissions('hazards');

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  const [hazards, setHazards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showAllSites, setShowAllSites] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [sort, setSort] = useState({ column: 'hazardnum', direction: 'asc' });
  const limit = 20;

  const { saveFavorite } = useFavoriteFilters('HAZARD');
  const handleSaveFavoriteFilter = async () => {
    const { value: name } = await Swal.fire({
      ...swalTheme(),
      title: t('favorites.namePrompt', 'Name this favorite filter'),
      input: 'text',
      inputPlaceholder: t('favorites.namePlaceholder', 'e.g. My Open WOs'),
      showCancelButton: true,
      confirmButtonText: t('common.save', 'Save'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      inputValidator: (value) => !value?.trim() && t('favorites.nameRequired', 'Please enter a name')
    });
    if (name && name.trim()) {
      saveFavorite(name, { searchTerm, all_sites: showAllSites, sortColumn: sort.column, sortDirection: sort.direction });
    }
  };

  useEffect(() => {
    const applyFavorite = (e) => {
      if (e.detail?.appName === 'HAZARD' && e.detail?.filters) {
        const { searchTerm: favSearchTerm, all_sites: favAllSites, sortColumn, sortDirection } = e.detail.filters;
        if (favSearchTerm !== undefined) setSearchTerm(favSearchTerm);
        if (favAllSites !== undefined) setShowAllSites(favAllSites);
        if (sortColumn !== undefined) setSort({ column: sortColumn, direction: sortDirection || 'asc' });
        setPage(1);
      }
    };
    window.addEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
    return () => window.removeEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/hazards', {
        params: {
          page, limit, search: searchTerm,
          all_sites: Boolean(showAllSites), site_id: sessionSite, org_id: sessionOrg,
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHazards(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(t('hazards.list.loadError', 'Failed to load hazards'));
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, showAllSites, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const handleSort = (column) => {
    setSort(prev => ({ column, direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const sortedHazards = [...hazards].sort((a, b) => {
    const valA = (a[sort.column] || '').toString().toLowerCase();
    const valB = (b[sort.column] || '').toString().toLowerCase();
    if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleClearFilters = () => {
    setSearchTerm("");
    setShowAllSites(false);
    setPage(1);
  };

  useEffect(() => {
    const openAllSites = () => { setShowAllSites(true); setPage(1); };
    const viewMySite = () => { setShowAllSites(false); setPage(1); };
    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    };
  }, []);

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[11px] uppercase font-black tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} dir={isRTL ? 'rtl' : 'ltr'} style={{ fontSize: `${fontSize}px` }}>
      {/* Toolbar */}
      <div className="bg-surface-card p-3 border-b border-line shadow-sm flex justify-between items-center sticky top-0 z-20">
        <div className="flex items-center gap-6 ml-4">
          <h2 className="text-primary text-lg italic font-black flex items-center gap-2">
            <ShieldAlert size={20} className="text-status-warning" /> {t('hazards.list.title', 'Hazards Master')}
            <RefreshCw size={16} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary/60 hover:text-primary'} ml-2`} onClick={fetchData} />
            <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase font-bold border ml-2 ${showAllSites ? 'bg-status-danger-bg text-status-danger border-status-danger/30' : 'bg-primary-soft text-primary border-primary/30'}`}>
              {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
            </span>
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-ink-secondary" size={14} />
            <input
              className="pl-9 pr-4 py-2 border border-line w-80 outline-none focus:border-primary shadow-inner rounded-sm"
              placeholder={t('hazards.list.searchPlaceholder', 'Search Hazard...')}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            />
          </div>
          <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
            <X size={14} /> {t('common.clear_filters', 'CLEAR FILTERS')}
          </button>
          <button onClick={handleSaveFavoriteFilter} className="flex items-center gap-2 px-4 py-2 text-status-warning hover:opacity-70 font-bold transition-colors">
            <Star size={14} /> {t('favorites.save', 'SAVE AS FAVORITE')}
          </button>
        </div>
        {canCreate && (
          <button onClick={() => navigate('/hazards/new')} className="bg-primary text-white px-6 py-2 rounded-sm font-bold flex items-center gap-2 shadow-md hover:bg-primary-dark transition-all mr-4">
            <Plus size={16} /> {t('hazards.list.new', 'NEW HAZARD')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-surface-card border border-line shadow-sm rounded-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
              <tr>
                <SortableHeader label={t('hazards.fields.hazardNum', 'Hazard Num')} sortKey="hazardnum" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('hazards.fields.description', 'Description')} sortKey="description" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('hazards.fields.type', 'Type')} sortKey="hazardtype" currentSort={sort} onSort={handleSort} align="center" isRTL={isRTL} />
                <th className="p-3 border-x border-white/10 text-center w-24">{t('hazards.fields.priority', 'Priority')}</th>
                <th className="p-3 border-x border-white/10 text-center w-28">{t('common.site', 'Site')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle font-bold">
              {loading ? (
                <tr><td colSpan="5" className="p-20 text-center italic text-primary font-black animate-pulse tracking-widest">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : sortedHazards.length === 0 ? (
                <tr><td colSpan="5" className="p-20 text-center italic text-ink-secondary text-xs font-black uppercase">{t('common.no_records', 'No records found')}</td></tr>
              ) : sortedHazards.map((h, i) => (
                <tr key={i} className="border-b border-line-subtle hover:bg-primary-soft cursor-pointer font-bold" onClick={() => navigate(`/hazards/${h.hazardnum}`)}>
                  <td className="p-2.5 border-r border-line-subtle text-primary font-black">{h.hazardnum}</td>
                  <td className="p-2.5 border-r border-line-subtle text-ink">{h.description}</td>
                  <td className="p-2.5 border-r border-line-subtle text-center text-status-warning font-black">{h.hazardtype}</td>
                  <td className="p-2.5 border-r border-line-subtle text-center text-ink-secondary">{h.priority}</td>
                  <td className="p-2.5 text-center text-ink-secondary">{h.siteid || '---'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className="bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[10px]">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Box size={14} className="text-sky" />
            <span>{t('common.showing', 'Showing')}: <span className="text-sky">{(page - 1) * limit + 1}-{Math.min(page * limit, total)}</span></span>
          </div>
          <div className="flex items-center gap-2 border-white/20 border-l pl-6">
            <Filter size={14} className="text-status-warning" />
            <span>{t('common.total', 'Total')}: <span className="text-status-warning">{total}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10">
            <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''} /></button>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''} /></button>
            <div className="px-4 text-[11px] font-mono border-x border-white/10 mx-1">
              <span className="text-status-warning text-sm">{page}</span> / {totalPages}
            </div>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronRight size={18} className={isRTL ? 'rotate-180' : ''} /></button>
            <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SortableHeader = ({ label, sortKey, currentSort, onSort, align = "left", isRTL }) => (
  <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-white/10 transition-colors ${align === 'center' ? 'text-center' : (isRTL ? 'text-right' : 'text-left')}`} onClick={() => onSort(sortKey)}>
    <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
      {label}
      <ArrowUpDown size={12} className={currentSort.column === sortKey ? 'text-status-warning' : 'text-white/30'} />
    </div>
  </th>
);

export default HazardList;
